import { type ComputedRef, type Ref } from "vue";
import { QuickSearchTipModel } from "../models/quick-search-models";

export interface KeyboardNavigationOptions {
    selectedIndex: Ref<number>;
    itemsLength: ComputedRef<number>;
    showAutocomplete: Ref<boolean>;
    searchText: Ref<string>;
    searchTips: Ref<QuickSearchTipModel[]>;
    onInput: (value: string) => void;
    onSubmit: () => void;
    onTipClick: (tip: QuickSearchTipModel) => void;
    hideAutocomplete: () => void;
    deactivate: () => void;
    getTipUrl: (tip: QuickSearchTipModel) => string | null;
}

export function useKeyboardNavigation(options: KeyboardNavigationOptions) {
    const {
        selectedIndex,
        itemsLength,
        showAutocomplete,
        searchText,
        searchTips,
        onInput,
        onSubmit,
        onTipClick,
        hideAutocomplete,
        deactivate,
        getTipUrl,
    } = options;

    function scrollSelectedIntoView() {
        if (selectedIndex.value >= 0) {
            const element = document.getElementById(`search-tip-${selectedIndex.value}`);
            element?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }

    function handleArrowDown(event: KeyboardEvent) {
        event.preventDefault();
        if (showAutocomplete.value && itemsLength.value > 0) {
            selectedIndex.value = selectedIndex.value >= itemsLength.value - 1 ? 0 : selectedIndex.value + 1;
            scrollSelectedIntoView();
        }
    }

    function handleArrowUp(event: KeyboardEvent) {
        event.preventDefault();
        if (showAutocomplete.value && itemsLength.value > 0) {
            selectedIndex.value = selectedIndex.value <= 0 ? itemsLength.value - 1 : selectedIndex.value - 1;
            scrollSelectedIntoView();
        }
    }

    function handleTab() {
        // Close autocomplete and allow normal tab flow
        if (showAutocomplete.value) {
            hideAutocomplete();
            selectedIndex.value = -1;
        }
    }

    function handleEnter(event: KeyboardEvent) {
        event.preventDefault();
        if (selectedIndex.value >= 0 && searchTips.value[selectedIndex.value]) {
            const selectedTip = searchTips.value[selectedIndex.value];
            onTipClick(selectedTip);
            const tipUrl = getTipUrl(selectedTip);

            if (tipUrl) {
                window.location.href = tipUrl;
            } else if (selectedTip.externalSystemId) {
                const gameElement = document.querySelector<HTMLElement>(
                    `[data-game-id="${selectedTip.externalSystemId}"]`
                );
                gameElement?.click();
            }
        } else {
            onSubmit();
        }
    }

    function handleEscape(event: KeyboardEvent) {
        event.preventDefault();
        if (selectedIndex.value >= 0) {
            selectedIndex.value = -1;
        } else if (showAutocomplete.value) {
            hideAutocomplete();
        } else if (searchText.value) {
            onInput("");
        } else {
            deactivate();
        }
    }

    function handleKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case "ArrowDown":
                handleArrowDown(event);
                break;
            case "ArrowUp":
                handleArrowUp(event);
                break;
            case "Tab":
                handleTab();
                break;
            case "Enter":
                handleEnter(event);
                break;
            case "Escape":
                handleEscape(event);
                break;
        }
    }

    return {
        handleKeyDown,
        scrollSelectedIntoView,
    };
}
